import { useEffect, useRef, useState } from "react";
import { Pause, Play, VolumeX } from "lucide-react";

export default function HeroVideo() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const userPaused = useRef(false);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      userPaused.current = true;
      video.pause();
      return;
    }

    // Play only while visible, save battery and bandwidth off-screen
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (userPaused.current) return;
        if (entry.isIntersecting) video.play().catch(() => {});
        else video.pause();
      },
      { threshold: 0.25 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      userPaused.current = false;
      video.play().catch(() => {});
    } else {
      userPaused.current = true;
      video.pause();
    }
  };

  return (
    <figure className="hv" data-ready={ready || undefined}>
      <div className="hv-bar hv-bar--top">
        <span className="hv-label"><i aria-hidden /> Ontoligent / Simulated system</span>
        <span className="hv-meta">Governed intelligence in motion</span>
      </div>

      <div className="hv-stage">
        <video
          ref={videoRef}
          className="hv-video"
          src="/ontoligent-illustration.mp4"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          disablePictureInPicture
          onLoadedData={() => setReady(true)}
          onCanPlay={() => setReady(true)}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          aria-label="Ontoligent enterprise intelligence platform illustration"
        />
        <div className="hv-loader" aria-hidden><span /></div>
      </div>

      <div className="hv-bar hv-bar--bottom">
        <span className="hv-status">Knowledge · Context · Reasoning · Action</span>
        <div className="hv-actions">
          <span className="hv-audio"><VolumeX size={13} aria-hidden /> Audio off</span>
          <button
            type="button"
            className="hv-btn"
            onClick={togglePlayback}
            aria-label={playing ? "Pause simulated system video" : "Play simulated system video"}
          >
            {playing ? <Pause size={14} aria-hidden /> : <Play size={14} aria-hidden />}
          </button>
        </div>
      </div>

      <style>{`
        .hv{margin:0;width:100%;overflow:hidden;border:1px solid hsl(var(--border));background:hsl(var(--panel));box-shadow:0 30px 80px -40px rgba(0,0,0,.6)}
        .hv-bar{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:10px 14px;font:10px/1.2 'IBM Plex Mono',monospace;letter-spacing:.1em;text-transform:uppercase;color:hsl(var(--text)/.72)}
        .hv-bar--top{border-bottom:1px solid hsl(var(--border))}
        .hv-bar--bottom{border-top:1px solid hsl(var(--border))}
        .hv-label{display:flex;align-items:center;gap:8px;min-width:0}
        .hv-label i{flex:none;width:7px;height:7px;border-radius:50%;background:hsl(var(--accent));box-shadow:0 0 0 4px hsl(var(--accent)/.14)}
        .hv-meta,.hv-status{min-width:0;overflow:hidden;white-space:nowrap;text-overflow:ellipsis}
        .hv-stage{position:relative;width:100%;aspect-ratio:var(--hv-ratio,16/9);background:#fff;overflow:hidden}
        .hv-video{position:absolute;inset:0;display:block;width:100%;height:100%;object-fit:contain;opacity:0;transition:opacity .8s ease}
        .hv[data-ready] .hv-video{opacity:1}
        .hv-loader{position:absolute;inset:0;display:grid;place-items:center;background:#fff;transition:opacity .6s ease,visibility .6s}
        .hv[data-ready] .hv-loader{opacity:0;visibility:hidden}
        .hv-loader span{width:48px;height:1px;overflow:hidden;background:rgba(10,25,60,.15)}
        .hv-loader span:after{content:"";display:block;width:50%;height:100%;background:hsl(var(--accent));animation:hv-load 1.1s ease-in-out infinite}
        @keyframes hv-load{from{transform:translateX(-110%)}to{transform:translateX(220%)}}
        .hv-actions{display:flex;flex:none;align-items:center;gap:10px}
        .hv-audio{display:flex;align-items:center;gap:6px}
        .hv-btn{display:grid;place-items:center;width:34px;height:34px;border:1px solid hsl(var(--border));background:transparent;color:hsl(var(--text));cursor:pointer;transition:border-color .2s,background .2s}
        .hv-btn:hover{border-color:hsl(var(--accent));background:hsl(var(--accent)/.14)}
        .hv-btn:focus-visible{outline:2px solid hsl(var(--accent));outline-offset:2px}
        @media(max-width:560px){.hv-audio,.hv-meta{display:none}.hv-bar{padding:8px 12px}}
        @media(prefers-reduced-motion:reduce){.hv-video{transition:none}.hv-loader span:after{animation:none}}
      `}</style>
    </figure>
  );
}
